import axiosInstance from 'config/axiosInstance';
import { FC, FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface RemoveTaskDialogProps {
  clientName: string;
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  taskId: number | null;
}

interface TaskSession {
  name: string;
  create_time: string;
  update_time: string;
}

const RemoveTaskDialog: FC<RemoveTaskDialogProps> = ({
  clientName,
  isOpen,
  onClose,
  onDelete,
  taskId,
}) => {
  const { t } = useTranslation('client');
  const dialogRef = useRef<HTMLDivElement>(null);
  const [taskSession, setTaskSession] = useState<TaskSession>();
  const [totalDuration, setTotalDuration] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (isOpen && taskId) {
      // Fetch task session details when the popup opens
      axiosInstance
        .get(`/v1/tasks/${taskId}/`)
        .then((response) => {
          const data = response.data;
          setTaskSession(data);

          // Fetch total hours spent from the new API
          return axiosInstance.get(
            `/v1/client_deliveries/${id}/tasks/${taskId}/hours_spent/`
          );
        })
        .then((response) => {
          const totalSeconds = response.data.total_hours_spent; // Assuming this returns seconds
          setTotalDuration(convertSecondsToHMS(totalSeconds));
        })
        .catch((error) => {
          console.error('Failed to fetch task session data:', error);
        });
    }
  }, [isOpen, taskId, id]);

  // Function to convert seconds to hours, minutes, and seconds
  const convertSecondsToHMS = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60); // Ensure seconds is an integer
    return { hours, minutes, seconds: remainingSeconds };
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await axiosInstance.delete(`v1/tasks/${taskId}/`);
      onClose();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  return (
    <div className="fixed z-20 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id="modal-headline"
                  >
                    {t('remove_task.title')}
                  </h3>
                  <button
                    onClick={() => onClose()}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <p className="m-4 sm:mx-6 sm:mt-6">
                  Are you sure you want to remove{' '}
                  <span className="text-primary/300 text-semibold text-subtitle-2">
                    {taskSession?.name}
                  </span>{' '}
                  from {clientName}? <br /> <br />
                  {t('remove_task.body_1')}
                  <br /> <br />
                  {t('remove_task.body_2')}
                </p>
                <form onSubmit={handleSubmit}>
                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-4 m-4 sm:m-6 sm:rounded-lg border sm:border-neutral/50 sm:p-4`}
                  >
                    <div>
                      <label className="block mb-1 text-overline font-regular text-neutral/100">
                        {t('remove_task.task_name')}
                      </label>
                      <p className="text-body-2 font-normal text-neutral/300 break-words">
                        {taskSession?.name}
                      </p>
                    </div>
                    <div>
                      <label className="block mb-1 text-overline font-regular text-neutral/100">
                        {t('remove_task.task_duration')}
                      </label>
                      <p className="text-body-2 font-normal text-neutral/300 break-words">
                        {totalDuration
                          ? `${totalDuration.hours} hrs ${totalDuration.minutes} min ${totalDuration.seconds} sec`
                          : 'Loading...'}
                      </p>
                    </div>
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-white text-subtitle-2 text-semibold text-neutral/75"
                      onClick={() => onClose()}
                    >
                      {t('action.cancel')}
                    </button>
                    <button
                      onClick={onDelete}
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12"
                    >
                      {t('action.understand')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveTaskDialog;
